<template>
  <v-dialog
    v-model="dialog"
    :max-width="options.width" @keydown.esc="cancel"
    persistent
  >
    <v-card>
      <v-card-title class="headline">
        {{ title }}

        <!-- only show if we're counting down -->
       <span v-if="countdown != null" class="ml-auto font-weight-light">
         {{ countdown }}s
       </span>
      </v-card-title>
      <v-card-text v-show="!!message">
        {{ message }}
        <slot name="additional-content"></slot>
      </v-card-text>
      <v-card-actions class="pt-0">
      <v-spacer></v-spacer>
      <v-btn color="primary" @click.native="agree">{{ options.confirm }}</v-btn>
      <v-btn color="secondary" @click.native="cancel">{{ options.cancel }}</v-btn>
    </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * Vuetify Confirm Dialog component
 *
 * Insert component where you want to use it:
 * <confirm ref="confirm"></confirm>
 *
 * Call it:
 * this.$refs.confirm.open('Delete', 'Are you sure?', { color: 'red' }).then((confirm) => {})
 *
 * inside export default {
 *   components: { confirm: confirm }
 * }
 * Or use await:
 * if (await this.$refs.confirm.open('Delete', 'Are you sure?', { color: 'red' })) {
 *   // yes
 * }
 * else {
 *   // cancel
 * }
 *
 * Alternatively you can place it in main App component and access it globally via this.$root.$confirm
 * <template>
 *   <v-app>
 *     ...
 *     <confirm ref="confirm"></confirm>
 *   </v-app>
 * </template>
 *
 * mounted() {
 *   this.$root.$confirm = this.$refs.confirm.open
 * }
 */
export default {
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    message: null,
    title: null,
    options: {
      color: 'primary',
      width: 400,
      confirm: 'Confirm',
      cancel: 'Cancel',
      timeout: null // in seconds, if set, will count down and auto-close
    },
    countdown: null,
    timerId: null
  }),
  methods: {
    open (title, message, options) {
      this.dialog = true
      this.title = title
      this.message = message
      this.options = Object.assign(this.options, options)

      if (this.options.timeout && Number(this.options.timeout) > 0) {
        // initialize the countdown display
        this.countdown = Number(this.options.timeout)

        // clear any existing timer (just in case)
        if (this.timerId) clearInterval(this.timerId)

        // start ticking
        this.timerId = setInterval(() => {
          this.countdown--
          if (this.countdown <= 0) {
            clearInterval(this.timerId)
            this.timerId = null
            this.cancel() // auto-cancel when it hits 0
          }
        }, 1000)
      } else {
      // no timeout hide the badge
        this.countdown = null
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve
        this.reject = reject
      })
    },
    agree () {
      // stop the timer if it's running
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
      this.resolve(true)
      this.dialog = false
    },
    cancel () {
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
      this.resolve(false)
      this.dialog = false
    }
  }
}
</script>

<style scoped>
/* float it to the right of the title: */
.v-card-title {
  display: flex;
  align-items: center;
}
</style>
